import { pdf } from "@react-pdf/renderer";
import { saveAs } from "file-saver";
import api from "../api";
import { branding } from "./branding";
import { sanitizeFileName } from "./validateFileName";
import ChatMessageExportPDF from "../components/ChatMessageExportPDF";
import defaultCompanyLogoUrl from "../assets/icons/logo_img.png";

const EMOJI_RE =
  /(\p{Regional_Indicator}{2}|[#*0-9]\uFE0F?\u20E3|\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?(?:\u200D\p{Extended_Pictographic}(?:\uFE0F|\uFE0E)?(?:\p{Emoji_Modifier})?)*)/gu;

const splitTextWithEmojis = (text) => {
  const value = String(text || "");
  const parts = [];
  let lastIndex = 0;

  for (const match of value.matchAll(EMOJI_RE)) {
    const index = match.index ?? 0;
    const emoji = match[0] || "";
    if (index > lastIndex) {
      parts.push({ type: "text", value: value.slice(lastIndex, index) });
    }
    parts.push({ type: "emoji", value: emoji });
    lastIndex = index + emoji.length;
  }

  if (lastIndex < value.length) {
    parts.push({ type: "text", value: value.slice(lastIndex) });
  }

  return parts;
};

const guessEmojiFont = () => {
  try {
    const ua = String(navigator?.userAgent || "");
    if (/Mac OS X|Macintosh/i.test(ua)) return "Apple Color Emoji";
    if (/Windows/i.test(ua)) return "Segoe UI Emoji";
    // common on Linux distros
    return "Noto Color Emoji";
  } catch {
    return "Segoe UI Emoji";
  }
};

const DEFAULT_DOCX_FONT = "Calibri";
const DEFAULT_DOCX_SIZE = 22; // 11pt (docx uses half-points)

const pngDimensionsFromArrayBuffer = (arrayBuffer) => {
  try {
    const bytes = new Uint8Array(arrayBuffer);
    // PNG signature: 89 50 4E 47 0D 0A 1A 0A
    if (
      bytes.length < 24 ||
      bytes[0] !== 0x89 ||
      bytes[1] !== 0x50 ||
      bytes[2] !== 0x4e ||
      bytes[3] !== 0x47
    ) {
      return null;
    }

    // IHDR width/height are at offsets 16..23 (big endian)
    const readU32BE = (offset) =>
      (bytes[offset] << 24) |
      (bytes[offset + 1] << 16) |
      (bytes[offset + 2] << 8) |
      bytes[offset + 3];

    const width = readU32BE(16);
    const height = readU32BE(20);
    if (!Number.isFinite(width) || !Number.isFinite(height)) return null;
    if (width <= 0 || height <= 0) return null;
    return { width, height };
  } catch {
    return null;
  }
};

const fitBox = ({ width, height }, maxWidth, maxHeight) => {
  if (!width || !height) return { width: maxWidth, height: maxHeight };
  const scale = Math.min(maxWidth / width, maxHeight / height);
  const w = Math.max(1, Math.round(width * scale));
  const h = Math.max(1, Math.round(height * scale));
  return { width: w, height: h };
};

const fitWithin = ({ width, height }, maxWidth, maxHeight) => {
  if (!width || !height) return { width: maxWidth, height: maxHeight };
  if (width <= maxWidth && height <= maxHeight) return { width, height };
  return fitBox({ width, height }, maxWidth, maxHeight);
};

const enterpriseBrandCache = new Map();
const imageDataUrlCache = new Map();
const imageArrayBufferCache = new Map();
const imagePngDataUrlCache = new Map();
const imagePngArrayBufferCache = new Map();

const isProbablySvgUrl = (url = "") => {
  const value = String(url || "").trim().toLowerCase();
  return value.endsWith(".svg") || value.includes("image/svg+xml");
};

const safeJsonParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const getCurrentUserFromStorage = () => {
  const userRaw = localStorage.getItem("user") || sessionStorage.getItem("user");
  return userRaw ? safeJsonParse(userRaw) : null;
};

const normalizeLogoUrl = (serverBaseUrl, enterpriseLogo) => {
  const value = String(enterpriseLogo || "").trim();
  if (!value) return "";

  if (/^https?:\/\//i.test(value)) return value;

  let cleaned = value.startsWith("/") ? value.slice(1) : value;
  if (!cleaned.includes("/") && !cleaned.startsWith("uploads/")) {
    cleaned = `uploads/${cleaned}`;
  }
  const base = String(serverBaseUrl || "").replace(/\/+$/, "");
  if (!base) return `/${cleaned}`;
  return `${base}/${cleaned}`;
};

const formatExportDateTime = () => {
  try {
    const d = new Date();
    return d.toLocaleString();
  } catch {
    return "";
  }
};

const formatFileStamp = (date = new Date()) => {
  const pad = (n) => String(n).padStart(2, "0");
  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());
  const ss = pad(date.getSeconds());
  return `${yyyy}-${mm}-${dd}_${hh}-${mi}-${ss}`;
};

const getEnterpriseBranding = async () => {
  const user = getCurrentUserFromStorage();
  const enterpriseId = user?.enterprise_fid;

  const cacheKey = enterpriseId ? String(enterpriseId) : "unknown";
  if (enterpriseBrandCache.has(cacheKey)) return enterpriseBrandCache.get(cacheKey);

  const fallback = {
    enterpriseId,
    enterpriseName: branding(user?.user_enterprise || user?.enterprise_name || "Enterprise"),
    logoUrl: "",
  };

  if (!enterpriseId) {
    enterpriseBrandCache.set(cacheKey, fallback);
    return fallback;
  }

  try {
    const res = await api.get("/enterprises", { skipLoader: true });
    const list = Array.isArray(res.data) ? res.data : [];
    const match = list.find(
      (e) => String(e?.enterprise_id) === String(enterpriseId)
    );

    const serverBaseUrl =
      process.env.REACT_APP_SERVER_URL || process.env.REACT_APP_API_URL || "";

    const enterpriseName = branding(
      match?.enterprise_name || fallback.enterpriseName || "Enterprise"
    );
    const logoUrl = normalizeLogoUrl(serverBaseUrl, match?.enterprise_logo);

    const result = { enterpriseId, enterpriseName, logoUrl };
    enterpriseBrandCache.set(cacheKey, result);
    return result;
  } catch {
    enterpriseBrandCache.set(cacheKey, fallback);
    return fallback;
  }
};

const fetchAsDataUrl = async (url) => {
  const key = String(url || "").trim();
  if (!key) return "";
  if (imageDataUrlCache.has(key)) return imageDataUrlCache.get(key);

  const res = await fetch(key, { mode: "cors" });
  if (!res.ok) throw new Error("logo_fetch_failed");
  const blob = await res.blob();

  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("logo_read_failed"));
    reader.readAsDataURL(blob);
  });

  imageDataUrlCache.set(key, dataUrl);
  return dataUrl;
};

const fetchAsArrayBuffer = async (url) => {
  const key = String(url || "").trim();
  if (!key) return null;
  if (imageArrayBufferCache.has(key)) return imageArrayBufferCache.get(key);

  const res = await fetch(key, { mode: "cors" });
  if (!res.ok) throw new Error("logo_fetch_failed");
  const buf = await res.arrayBuffer();
  imageArrayBufferCache.set(key, buf);
  return buf;
};

const svgUrlToPngBlob = async (svgUrl, width = 128, height = 128) => {
  const res = await fetch(String(svgUrl || "").trim(), { mode: "cors" });
  if (!res.ok) throw new Error("svg_fetch_failed");
  const svgText = await res.text();

  const svgDataUrl =
    "data:image/svg+xml;charset=utf-8," + encodeURIComponent(svgText);

  const img = await new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error("svg_image_load_failed"));
    image.src = svgDataUrl;
  });

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("canvas_ctx_missing");
  ctx.clearRect(0, 0, width, height);
  // Preserve aspect ratio ("contain") so the logo isn't stretched.
  const imgW = Number(img.width || 0);
  const imgH = Number(img.height || 0);
  if (imgW > 0 && imgH > 0) {
    const scale = Math.min(width / imgW, height / imgH);
    const drawW = imgW * scale;
    const drawH = imgH * scale;
    const x = (width - drawW) / 2;
    const y = (height - drawH) / 2;
    ctx.drawImage(img, x, y, drawW, drawH);
  } else {
    ctx.drawImage(img, 0, 0, width, height);
  }

  const blob = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("png_blob_failed"))), "image/png");
  });

  return blob;
};

const fetchAsPngDataUrl = async (url) => {
  const key = String(url || "").trim();
  if (!key) return "";
  if (imagePngDataUrlCache.has(key)) return imagePngDataUrlCache.get(key);

  if (!isProbablySvgUrl(key)) {
    const dataUrl = await fetchAsDataUrl(key);
    imagePngDataUrlCache.set(key, dataUrl);
    return dataUrl;
  }

  // Logos are often wide; use a wide canvas so the result stays crisp and undistorted.
  const blob = await svgUrlToPngBlob(key, 600, 200);
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("png_read_failed"));
    reader.readAsDataURL(blob);
  });

  imagePngDataUrlCache.set(key, dataUrl);
  return dataUrl;
};

const fetchAsPngArrayBuffer = async (url) => {
  const key = String(url || "").trim();
  if (!key) return null;
  if (imagePngArrayBufferCache.has(key)) return imagePngArrayBufferCache.get(key);

  if (!isProbablySvgUrl(key)) {
    const buf = await fetchAsArrayBuffer(key);
    imagePngArrayBufferCache.set(key, buf);
    return buf;
  }

  const blob = await svgUrlToPngBlob(key, 600, 200);
  const buf = await blob.arrayBuffer();
  imagePngArrayBufferCache.set(key, buf);
  return buf;
};

const buildExportFileBaseName = ({ enterpriseName, messageId, exportedAt }) => {
  const stamp = formatFileStamp(exportedAt || new Date());
  const base = `${enterpriseName || "Enterprise"} ${stamp}${messageId ? ` ${messageId}` : ""}`
    .replace(/\s+/g, " ")
    .trim();
  return (sanitizeFileName(base).slice(0, 90) || "Chat Export").replace(
    /\s+/g,
    "_",
  );
};

export const exportBotMessageAsPdf = async ({
  markdown = "",
  messageId,
  title,
}) => {
  const brand = await getEnterpriseBranding();
  const exportedAt = new Date();

  let logoDataUrl = "";
  let logoDimensions = null;
  try {
    const url = brand.logoUrl || defaultCompanyLogoUrl;
    logoDataUrl = url ? await fetchAsPngDataUrl(url) : "";
    const buf = url ? await fetchAsPngArrayBuffer(url) : null;
    const dims = buf ? pngDimensionsFromArrayBuffer(buf) : null;
    // Keep the intrinsic size unless it would overflow the header.
    logoDimensions = dims ? fitWithin(dims, 240, 54) : null;
  } catch {
    logoDataUrl = "";
    logoDimensions = null;
  }

  const exportedAtLabel = formatExportDateTime()
    ? `Exported: ${formatExportDateTime()}`
    : "";

  const blob = await pdf(
    <ChatMessageExportPDF
      enterpriseName={brand.enterpriseName}
      logoDataUrl={logoDataUrl}
      logoWidth={logoDimensions?.width}
      logoHeight={logoDimensions?.height}
      title={title === undefined || title === null ? "Bot Response" : title}
      subtitle="Chat Message Export"
      exportedAtLabel={exportedAtLabel}
      markdown={markdown}
    />
  ).toBlob();

  const baseName = buildExportFileBaseName({
    enterpriseName: brand.enterpriseName,
    messageId,
    exportedAt,
  });
  saveAs(blob, `${baseName}.pdf`);
};

export const exportBotMessageAsDocx = async ({
  markdown = "",
  messageId,
  title,
}) => {
  const brand = await getEnterpriseBranding();
  const exportedAt = new Date();

  const docxModule = await import("docx");
  const {
    AlignmentType,
    Document,
    Footer,
    Header,
    HeadingLevel,
    ImageRun,
    Packer,
    Paragraph,
    Table,
    TableCell,
    TableRow,
    WidthType,
    PageNumber,
    ShadingType,
    TextRun,
  } = docxModule;

  const exportedAtLabel = formatExportDateTime()
    ? `Exported: ${formatExportDateTime()}`
    : "";

  let logoBuffer = null;
  try {
    const url = brand.logoUrl || defaultCompanyLogoUrl;
    logoBuffer = url ? await fetchAsPngArrayBuffer(url) : null;
  } catch {
    logoBuffer = null;
  }

  const logoTransform = (() => {
    if (!logoBuffer) return null;
    const dims = pngDimensionsFromArrayBuffer(logoBuffer);
    if (!dims) return { width: 180, height: 54 };
    // Use intrinsic size, but shrink only if it would overflow the header area.
    return fitWithin(dims, 240, 54);
  })();

  const { unified } = await import("unified");
  const remarkParse = (await import("remark-parse")).default;
  const remarkGfm = (await import("remark-gfm")).default;

  const tree = unified().use(remarkParse).use(remarkGfm).parse(markdown || "");

  const nodeText = (node) => {
    if (!node) return "";
    if (node.type === "text") return node.value || "";
    if (node.type === "inlineCode") return node.value || "";
    if (Array.isArray(node.children)) return node.children.map(nodeText).join("");
    return "";
  };

  const toRuns = (node, active = {}) => {
    if (!node) return [];

    if (node.type === "text") {
      const emojiFont = guessEmojiFont();
      return splitTextWithEmojis(node.value || "").map((p) => {
        const isEmoji = p.type === "emoji";
        return new TextRun({
          text: p.value,
          bold: Boolean(active.bold),
          italics: Boolean(active.italics),
          font: isEmoji ? emojiFont : DEFAULT_DOCX_FONT,
          size: DEFAULT_DOCX_SIZE,
        });
      });
    }

    if (node.type === "inlineCode") {
      const emojiFont = guessEmojiFont();
      return splitTextWithEmojis(node.value || "").map((p) => {
        const isEmoji = p.type === "emoji";
        return new TextRun({
          text: p.value,
          font: isEmoji ? emojiFont : "Consolas",
          bold: Boolean(active.bold),
          italics: Boolean(active.italics),
          size: DEFAULT_DOCX_SIZE,
        });
      });
    }

    if (node.type === "strong") {
      return (node.children || []).flatMap((c) => toRuns(c, { ...active, bold: true }));
    }

    if (node.type === "emphasis") {
      return (node.children || []).flatMap((c) =>
        toRuns(c, { ...active, italics: true })
      );
    }

    if (node.type === "link") {
      const label = nodeText(node) || node.url || "";
      return [
        new TextRun({
          text: label,
          bold: Boolean(active.bold),
          italics: Boolean(active.italics),
          color: "2563EB",
          font: DEFAULT_DOCX_FONT,
          size: DEFAULT_DOCX_SIZE,
        }),
      ];
    }

    if (node.type === "break") {
      return [new TextRun({ text: "\n", font: DEFAULT_DOCX_FONT, size: DEFAULT_DOCX_SIZE })];
    }

    if (Array.isArray(node.children)) {
      return node.children.flatMap((c) => toRuns(c, active));
    }

    return [];
  };

  const blocks = [];

  const pushParagraphFromChildren = (children, opts = {}) => {
    const runs = (children || []).flatMap((c) => toRuns(c));
    blocks.push(new Paragraph({ children: runs, ...opts }));
  };

  const walk = (node, ctx = {}) => {
    if (!node) return;

    switch (node.type) {
      case "paragraph":
        pushParagraphFromChildren(node.children, {
          spacing: { after: 200 },
        });
        return;

      case "heading": {
        // Keep headings visually distinct but still 11pt as requested.
        const runs = (node.children || []).flatMap((c) => toRuns(c, { bold: true }));
        blocks.push(
          new Paragraph({
            children: runs,
            spacing: { before: 160, after: 120 },
          })
        );
        return;
      }

      case "code": {
        const lines = String(node.value || "").split("\n");
        const codeRuns = [];
        lines.forEach((line, idx) => {
          codeRuns.push(
            new TextRun({
              text: line,
              font: "Consolas",
              color: "E2E8F0",
              size: DEFAULT_DOCX_SIZE,
            })
          );
          if (idx < lines.length - 1) codeRuns.push(new TextRun({ break: 1 }));
        });

        blocks.push(
          new Paragraph({
            children: codeRuns,
            shading: {
              type: ShadingType.CLEAR,
              color: "auto",
              fill: "0B1220",
            },
            spacing: { before: 120, after: 200 },
          })
        );
        return;
      }

      case "blockquote": {
        (node.children || []).forEach((c) => walk(c, { ...ctx, blockquote: true }));
        return;
      }

      case "thematicBreak":
        blocks.push(
          new Paragraph({
            children: [
              new TextRun({
                text: "—".repeat(24),
                color: "94A3B8",
                font: DEFAULT_DOCX_FONT,
                size: DEFAULT_DOCX_SIZE,
              }),
            ],
            spacing: { before: 120, after: 200 },
            alignment: AlignmentType.CENTER,
          })
        );
        return;

      case "list": {
        const ordered = Boolean(node.ordered);
        let orderedCounter = Number(node.start || 1);

        (node.children || []).forEach((li) => {
          walk(li, {
            ...ctx,
            list: true,
            listOrdered: ordered,
            listNumber: ordered ? orderedCounter++ : null,
          });
        });
        return;
      }

      case "listItem": {
        const flattened = (node.children || []).flatMap((c) =>
          c.type === "paragraph" ? c.children || [] : [c]
        );

        if (ctx.list && !ctx.listOrdered) {
          blocks.push(
            new Paragraph({
              children: flattened.flatMap((c) => toRuns(c)),
              bullet: { level: 0 },
              spacing: { after: 80 },
            })
          );
          return;
        }

        if (ctx.list && ctx.listOrdered) {
          const prefix = `${ctx.listNumber}. `;
          blocks.push(
            new Paragraph({
              children: [
                new TextRun({ text: prefix, font: DEFAULT_DOCX_FONT, size: DEFAULT_DOCX_SIZE }),
                ...flattened.flatMap((c) => toRuns(c)),
              ],
              spacing: { after: 80 },
            })
          );
          return;
        }

        blocks.push(
          new Paragraph({
            children: flattened.flatMap((c) => toRuns(c)),
            spacing: { after: 120 },
          })
        );
        return;
      }

      default:
        if (Array.isArray(node.children)) {
          node.children.forEach((c) => walk(c, ctx));
        }
    }
  };

  (tree.children || []).forEach((c) => walk(c, {}));

  const headerChildren = [
    new Paragraph({
      alignment: AlignmentType.LEFT,
      children: [
        ...(logoBuffer
          ? [
              new ImageRun({
                data: logoBuffer,
                transformation: logoTransform || { width: 140, height: 36 },
              }),
            ]
          : [
              new TextRun({
                text: brand.enterpriseName || "Enterprise",
                bold: true,
                font: DEFAULT_DOCX_FONT,
                size: DEFAULT_DOCX_SIZE,
              }),
            ]),
      ],
    }),
  ];

  const footerChildren = [
    new Table({
      width: { size: 100, type: WidthType.PERCENTAGE },
      borders: {
        top: { style: "none", size: 0, color: "FFFFFF" },
        bottom: { style: "none", size: 0, color: "FFFFFF" },
        left: { style: "none", size: 0, color: "FFFFFF" },
        right: { style: "none", size: 0, color: "FFFFFF" },
        insideHorizontal: { style: "none", size: 0, color: "FFFFFF" },
        insideVertical: { style: "none", size: 0, color: "FFFFFF" },
      },
      rows: [
        new TableRow({
          children: [
            new TableCell({
              width: { size: 30, type: WidthType.PERCENTAGE },
              borders: { top: { style: "none" }, bottom: { style: "none" }, left: { style: "none" }, right: { style: "none" } },
              children: [
                new Paragraph({
                  alignment: AlignmentType.LEFT,
                  children: [],
                }),
              ],
            }),
            new TableCell({
              width: { size: 40, type: WidthType.PERCENTAGE },
              borders: { top: { style: "none" }, bottom: { style: "none" }, left: { style: "none" }, right: { style: "none" } },
              children: [
                new Paragraph({
                  alignment: AlignmentType.CENTER,
                  children: [
                    new TextRun({ text: "Confidential", color: "64748B" }),
                  ],
                }),
              ],
            }),
            new TableCell({
              width: { size: 30, type: WidthType.PERCENTAGE },
              borders: { top: { style: "none" }, bottom: { style: "none" }, left: { style: "none" }, right: { style: "none" } },
              children: [
                new Paragraph({
                  alignment: AlignmentType.RIGHT,
                  children: [
                    new TextRun({ text: "Page - ", color: "64748B" }),
                    new TextRun({ children: [PageNumber.CURRENT], color: "64748B" }),
                  ],
                }),
              ],
            }),
          ],
        }),
      ],
    }),
  ];

  const doc = new Document({
    // If caller passes an empty string, do not render a title heading.
    sections: [
      {
        headers: { default: new Header({ children: headerChildren }) },
        footers: { default: new Footer({ children: footerChildren }) },
        children: [
          ...(() => {
            const titleText = title === undefined || title === null ? "Bot Response" : String(title);
            if (!titleText) return [];
            return [
              new Paragraph({
                text: titleText,
                heading: HeadingLevel.HEADING_2,
                spacing: { after: 220 },
              }),
            ];
          })(),
          ...blocks,
        ],
      },
    ],
  });

  const blob = await Packer.toBlob(doc);
  const baseName = buildExportFileBaseName({
    enterpriseName: brand.enterpriseName,
    messageId,
    exportedAt,
  });
  saveAs(blob, `${baseName}.docx`);
};
